/**
 * Industry-specific lead filter configuration.
 * Study Abroad (1) and Education (2) share the same filters.
 * Video Production (4) and Academy (5) have their own section sets and labels.
 */

export const LEAD_FILTER_INDUSTRY_IDS = {
  STUDY_ABROAD: 1,
  EDUCATION: 2,
  VIDEO_PRODUCTION: 4,
  ACADEMY: 5,
} as const;

export type LeadFilterSectionId =
  | "dates"
  | "sourceCampaign"
  | "basic"
  | "funnel"
  | "tracks"
  | "activity"
  | "calls"
  | "targets"
  | "followup"
  | "academic"
  | "leadPreference"
  | "preference"
  | "choice"
  | "payment";

/** Labels for the "choice" section (reused as Service Info / Program Info per industry) */
export interface ChoiceSectionLabels {
  sectionTitle: string;
  hasLabel: string;
  hasDescription: string;
  numberLabel: string;
  /**
   * Plural noun for ONE counted item, used in the "Number of …" scope hint
   * ("Counting only deals in …" / "… services in …"). The count is scoped to
   * the deals sitting in the picked Stage / Status / Sub-status, so the hint
   * has to say which unit it is counting.
   */
  countNoun: string;
  appliedInLabel: string;
  lastUpdatedLabel: string;
  keywords: string;
}

// "Deal", not "Choice" (2026-09-04, user request). The underlying model was
// always `Child` = a deal; "choice" was study-abroad-only wording that read as a
// different concept from the deals every other surface (board, reports, export,
// count-scope hint two files over) already talked about. The `choice*` filter
// KEYS are untouched — they are the wire contract with the server. Old wording
// stays in `keywords` so anyone searching "choices" still finds the section.
const STUDY_ABROAD_CHOICE_LABELS: ChoiceSectionLabels = {
  sectionTitle: "Deal Info",
  hasLabel: "Has Deals",
  hasDescription: "Show only leads who added at least one deal",
  numberLabel: "Number of deals",
  countNoun: "deals",
  appliedInLabel: "Deals applied in",
  lastUpdatedLabel: "Deals last updated",
  keywords:
    "deal deals choice choices selection application has deals has choices number of deals number of choices deals applied in choices applied in deals last updated choices last updated",
};

const VIDEO_PRODUCTION_CHOICE_LABELS: ChoiceSectionLabels = {
  sectionTitle: "Service Info",
  hasLabel: "Has Service",
  hasDescription: "Show only leads who have at least one service selected",
  numberLabel: "Number of services",
  countNoun: "services",
  appliedInLabel: "Services applied in",
  lastUpdatedLabel: "Services last updated",
  keywords:
    "service services video production project has service number of services services applied in services last updated",
};

const ACADEMY_CHOICE_LABELS: ChoiceSectionLabels = {
  sectionTitle: "Program Info",
  hasLabel: "Has Program",
  hasDescription:
    "Show only leads who have at least one program/variant selected",
  numberLabel: "Number of programs",
  countNoun: "programs",
  appliedInLabel: "Programs applied in",
  lastUpdatedLabel: "Programs last updated",
  keywords:
    "program programs academy variant course has program number of programs programs applied in programs last updated",
};

/**
 * Which CROSS-INDUSTRY sections are enabled per industry. Industry-specific
 * sections (`academic`, `preference`) are not listed here — they live in the
 * registry under `src/features/leads/components/leadFilter/registry/` and
 * each industry contributes them via `src/module/<industry>/leadFilters.tsx`.
 */
const CORE_SECTIONS_ALL: LeadFilterSectionId[] = [
  "dates",
  "sourceCampaign",
  "basic",
  "funnel",
  "tracks",
  "activity",
  "calls",
  "targets",
  "followup",
  "leadPreference",
  "choice",
  "payment",
];

const SECTION_VISIBILITY: Record<number, LeadFilterSectionId[]> = {
  [LEAD_FILTER_INDUSTRY_IDS.STUDY_ABROAD]: CORE_SECTIONS_ALL,
  [LEAD_FILTER_INDUSTRY_IDS.EDUCATION]: CORE_SECTIONS_ALL,
  [LEAD_FILTER_INDUSTRY_IDS.VIDEO_PRODUCTION]: CORE_SECTIONS_ALL,
  [LEAD_FILTER_INDUSTRY_IDS.ACADEMY]: CORE_SECTIONS_ALL,
};

/** Choice section labels per industry */
const CHOICE_LABELS_BY_INDUSTRY: Record<number, ChoiceSectionLabels> = {
  [LEAD_FILTER_INDUSTRY_IDS.STUDY_ABROAD]: STUDY_ABROAD_CHOICE_LABELS,
  [LEAD_FILTER_INDUSTRY_IDS.EDUCATION]: STUDY_ABROAD_CHOICE_LABELS,
  [LEAD_FILTER_INDUSTRY_IDS.VIDEO_PRODUCTION]: VIDEO_PRODUCTION_CHOICE_LABELS,
  [LEAD_FILTER_INDUSTRY_IDS.ACADEMY]: ACADEMY_CHOICE_LABELS,
};

/** Preference section keywords per industry (for filter search) */
export const PREFERENCE_KEYWORDS_BY_INDUSTRY: Record<number, string> = {
  [LEAD_FILTER_INDUSTRY_IDS.STUDY_ABROAD]: [
    "study",
    "preference",
    "university",
    "course",
    "custom",
    "custom field",
    "destination",
    "abroad",
  ].join(" "),
  [LEAD_FILTER_INDUSTRY_IDS.EDUCATION]: [
    "study",
    "preference",
    "university",
    "course",
    "custom",
    "custom field",
    "destination",
  ].join(" "),
  [LEAD_FILTER_INDUSTRY_IDS.VIDEO_PRODUCTION]: [
    "service",
    "preference",
    "video",
    "production",
    "service variant",
    "variant",
    "project type",
    "custom",
    "custom field",
  ].join(" "),
  [LEAD_FILTER_INDUSTRY_IDS.ACADEMY]: [
    "program",
    "preference",
    "academy",
    "program variant",
    "variant",
    "course",
    "batch",
    "custom",
    "custom field",
  ].join(" "),
};

const DEFAULT_CHOICE_LABELS = STUDY_ABROAD_CHOICE_LABELS;

export function getLeadFilterSectionsForIndustry(
  industryId: number | undefined
): LeadFilterSectionId[] {
  if (industryId == null) return CORE_SECTIONS_ALL;
  return SECTION_VISIBILITY[industryId] ?? CORE_SECTIONS_ALL;
}

export function getChoiceSectionLabelsForIndustry(
  industryId: number | undefined
): ChoiceSectionLabels {
  if (industryId == null) return DEFAULT_CHOICE_LABELS;
  return CHOICE_LABELS_BY_INDUSTRY[industryId] ?? DEFAULT_CHOICE_LABELS;
}
